import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Building2, Server, Phone, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { login, clearError } from '../store/authSlice';
import { fetchPublicOrg, setSlug } from '../store/orgSlice';
import { mediaUrl } from '../utils/media';
import { useT } from '../utils/translate';
import LangDropdown from '../components/LangDropdown';

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const t = useT();
  const { loading, error } = useSelector((s) => s.auth);
  const { slug: detectedSlug, data: org, loading: orgLoading, checked: orgChecked } = useSelector((s) => s.org);

  const [slugInput, setSlugInput] = useState(detectedSlug || '');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (error) { toast.error(error); dispatch(clearError()); }
  }, [error, dispatch]);

  // Subdomen bo'lmagan holatda (dev/apex) foydalanuvchi qo'lda slug kiritadi —
  // shuni ham branding uchun tekshiramiz (bosilgandan 500ms keyin).
  useEffect(() => {
    if (detectedSlug) return; // subdomendan aniqlangan bo'lsa — App.js allaqachon yuklagan
    if (!slugInput || slugInput.trim().length < 3) return;
    const h = setTimeout(() => {
      dispatch(setSlug(slugInput.trim()));
      dispatch(fetchPublicOrg(slugInput.trim()));
    }, 500);
    return () => clearTimeout(h);
  }, [slugInput, detectedSlug, dispatch]);

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
    setPhone(digits);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(login({
      slug: slugInput.trim(),
      phone: '+998' + phone,
      password,
    })).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') navigate('/', { replace: true });
    });
  };

  const branded = Boolean(detectedSlug);

  return (
    <div className={`min-h-screen min-h-screen-safe flex flex-col ${branded ? 'bg-primary-700' : 'bg-white'}`}>
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-6 sm:pb-10 safe-top [--safe-pad:1.5rem] sm:[--safe-pad:2.5rem]">
        {/* Logo + tashkilot nomi — kartadan alohida, markazda va yiriqroq */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className={`w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden shadow-lg ${
            branded ? 'bg-white/15 ring-4 ring-white/10' : 'bg-surface-100 ring-4 ring-surface-100'
          }`}>
            {org?.logo
              ? <img src={mediaUrl(org.logo)} alt={org.name} className="w-full h-full object-cover" />
              : <Building2 className={`w-9 h-9 ${branded ? 'text-white' : 'text-ink-secondary'}`} />}
          </div>
          <h2 className={`font-bold text-2xl tracking-tight mt-4 ${branded ? 'text-white' : 'text-ink'}`}>
            {org?.name || t('login.subtitle')}
          </h2>
          {org?.name && (
            <p className={`text-sm mt-1 ${branded ? 'text-primary-200' : 'text-ink-tertiary'}`}>{t('login.subtitle')}</p>
          )}
        </div>

        <div className={`w-full max-w-md bg-white rounded-2xl shadow-modal p-6 sm:p-8 ${branded ? '' : 'border border-surface-200'}`}>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-xl font-bold text-ink">{t('login.title')}</h1>
            <LangDropdown />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!detectedSlug && (
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">{t('login.server')}</label>
                <div className="relative">
                  <Server className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
                  <input
                    type="text" value={slugInput}
                    onChange={e => setSlugInput(e.target.value)} required
                    placeholder={t('login.serverPlaceholder')}
                    className="input input-with-icon"
                    autoComplete="off" autoCapitalize="none"
                  />
                </div>
                {orgLoading && <p className="text-xs text-ink-tertiary mt-1">{t('login.checking')}</p>}
                {orgChecked && !org && slugInput.trim().length >= 3 && (
                  <p className="text-xs text-red-600 mt-1">{t('login.notFound')}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">{t('login.phone')}</label>
              <div className="flex">
                <div className="flex items-center gap-1.5 px-3 bg-surface-100 border border-r-0 border-surface-200 rounded-l-lg text-sm font-medium text-ink-secondary select-none shrink-0">
                  <Phone className="w-3.5 h-3.5 text-ink-tertiary" />
                  +998
                </div>
                <input
                  type="tel" value={phone}
                  onChange={handlePhoneChange} required
                  placeholder={t('login.phonePlaceholder')}
                  className="input rounded-l-none flex-1"
                  autoComplete="tel" inputMode="numeric" maxLength={9}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">{t('login.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="••••••"
                  className="input input-with-icon pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-tertiary hover:text-ink transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary btn-lg w-full mt-2">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('login.submitting')}</> : t('login.submit')}
            </button>
          </form>
        </div>
      </div>

      <div className="pb-6 safe-bottom [--safe-pad:1.5rem]" />
    </div>
  );
}
